import type { SyntheticEvent } from "react";
import { useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { docUrl } from "../lib/docLinks";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { formatDateTime } from "../lib/format";
import { queryKeys } from "../lib/queryKeys";
import {
  uniqueIds,
  useUserDirectoryQuery,
  userDisplayName,
  userMap,
} from "./shared/userDirectory";
import {
  Button,
  Code,
  Copyable,
  HelpTip,
  Inline,
  InlineError,
  KeyValue,
  Page,
  Section,
  Skeleton,
  Spinner,
  Stack,
  Text,
  TextInput,
  useToast,
} from "../ui";

type MeResponse = components["schemas"]["MeResponse"];
type OrganizationPatch = components["schemas"]["OrganizationPatch"];
type OrganizationResponse = components["schemas"]["OrganizationResponse"];
type OrganizationLimits = components["schemas"]["OrganizationLimitsResponse"];

function organizationDetailsRows(
  organization: OrganizationResponse,
  me: MeResponse,
  ownerName?: string,
) {
  const ownerId = organization.owner_app_user_id;
  const ownerValue = ownerId ? (
    <Inline align="baseline" gap={2}>
      <span title={`User ID: ${ownerId}`}>{ownerName ?? "Loading owner"}</span>
      {ownerId === me.user.id ? (
        <Text as="span" variant="muted">
          (you)
        </Text>
      ) : null}
    </Inline>
  ) : (
    <Code>—</Code>
  );

  return [
    {
      label: "Organization ID",
      value: <Copyable label="Organization ID" value={organization.id} />,
    },
    { label: "Owner", value: ownerValue },
    { label: "Created", value: formatDateTime(organization.created_at) },
    { label: "Updated", value: formatDateTime(organization.updated_at) },
  ];
}

export function OrganizationSettings() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [displayName, setDisplayName] = useState("");
  const [loadedOrganizationId, setLoadedOrganizationId] = useState<
    string | null
  >(null);

  const me = useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/me");
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  const organizationId = me.data?.organization.id;
  // A self-managed deployment-operator session (docs/05 §6a) is limited to
  // the limits read; the customer organization routes deny it, so the org
  // detail/patch queries never run for one.
  const isOperator = (me.data?.operator_capabilities.length ?? 0) > 0;
  const organization = useQuery({
    enabled: Boolean(organizationId) && !isOperator,
    queryKey: queryKeys.organizations.detail(organizationId ?? "pending"),
    queryFn: async () => {
      if (!organizationId) {
        throw new Error("Organization id is required.");
      }
      const { data, error } = await apiClient.GET("/v1/organizations/{id}", {
        params: { path: { id: organizationId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  const ownerId = organization.data?.owner_app_user_id;
  const ownerUsers = useUserDirectoryQuery(
    uniqueIds([ownerId]).join(","),
    "organization-settings-owner",
  );
  const owner = userMap(ownerUsers.data?.items ?? []).get(ownerId ?? "");

  const limits = useQuery({
    enabled: Boolean(organizationId),
    queryKey: queryKeys.organizationLimits.detail(organizationId ?? "pending"),
    queryFn: async () => {
      if (!organizationId) {
        throw new Error("Organization id is required.");
      }
      const { data, error } = await apiClient.GET(
        "/v1/organizations/{id}/limits",
        { params: { path: { id: organizationId } } },
      );
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  if (
    organization.data !== undefined &&
    organization.data.id !== loadedOrganizationId
  ) {
    setDisplayName(organization.data.display_name);
    setLoadedOrganizationId(organization.data.id);
  }

  const saveOrganization = useMutation({
    mutationFn: async (nextDisplayName: string) => {
      if (!organizationId) {
        throw new Error("Organization id is required.");
      }
      const body = { display_name: nextDisplayName } as OrganizationPatch;
      const { data, error } = await apiClient.PATCH("/v1/organizations/{id}", {
        body,
        params: { path: { id: organizationId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (updatedOrganization) => {
      setDisplayName(updatedOrganization.display_name);
      queryClient.setQueryData(
        queryKeys.organizations.detail(updatedOrganization.id),
        updatedOrganization,
      );
      toast.success("Organization updated");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.me }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.organizations.all,
        }),
      ]);
    },
    onError: (error) => {
      toast.error("Organization update failed", {
        description: requestErrorDescription(
          error,
          "Organization request failed.",
        ),
      });
    },
  });

  const isPending =
    me.isPending ||
    (organizationId != null &&
      ((!isOperator && organization.isPending) || limits.isPending));
  const errorMessage =
    (me.isError ? me.error.message : undefined) ??
    (organization.isError ? organization.error.message : undefined);
  const currentOrganization = organization.data;
  const isOwner =
    currentOrganization != null &&
    me.data != null &&
    currentOrganization.owner_app_user_id === me.data.user.id;
  const normalizedDisplayName = displayName.trim();
  const isUnchanged =
    currentOrganization == null ||
    normalizedDisplayName === currentOrganization.display_name;
  const isSaveDisabled =
    isUnchanged ||
    normalizedDisplayName.length === 0 ||
    saveOrganization.isPending;
  const isSaved =
    saveOrganization.isSuccess &&
    saveOrganization.data.display_name === normalizedDisplayName;

  return (
    <OrganizationSettingsView
      displayName={displayName}
      errorMessage={errorMessage}
      isOwner={isOwner}
      isPending={isPending}
      isSaveDisabled={isSaveDisabled}
      isSaved={isSaved}
      isSavePending={saveOrganization.isPending}
      me={me.data}
      ownerName={owner ? userDisplayName(owner) : undefined}
      onDisplayNameChange={setDisplayName}
      onSubmit={() => {
        saveOrganization.mutate(normalizedDisplayName);
      }}
      isOperator={isOperator}
      limits={limits.data}
      limitsErrorMessage={limits.isError ? limits.error.message : undefined}
      organization={currentOrganization}
      saveErrorMessage={
        saveOrganization.isError ? saveOrganization.error.message : undefined
      }
    />
  );
}

export interface OrganizationSettingsViewProps {
  displayName: string;
  errorMessage?: string;
  isOperator?: boolean;
  isOwner: boolean;
  isPending: boolean;
  isSaveDisabled: boolean;
  isSaved: boolean;
  isSavePending: boolean;
  limits?: OrganizationLimits;
  limitsErrorMessage?: string;
  me?: MeResponse;
  ownerName?: string;
  onDisplayNameChange: (value: string) => void;
  onSubmit: () => void;
  organization?: OrganizationResponse;
  saveErrorMessage?: string;
}

export function OrganizationSettingsView({
  displayName,
  errorMessage,
  isOperator = false,
  isOwner,
  isPending,
  isSaveDisabled,
  isSaved,
  isSavePending,
  limits,
  limitsErrorMessage,
  me,
  ownerName,
  onDisplayNameChange,
  onSubmit,
  organization,
  saveErrorMessage,
}: OrganizationSettingsViewProps) {
  function handleSubmit(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!isSaveDisabled) {
      onSubmit();
    }
  }

  const limitsSection = (
    <Section heading="Limits">
      {limitsErrorMessage ? (
        <InlineError>{limitsErrorMessage}</InlineError>
      ) : (
        <Stack gap={2}>
          <KeyValue
            rows={[
              {
                label: (
                  <Inline align="center" gap={2}>
                    <span>Concurrency ceiling</span>
                    <HelpTip
                      docHref={docUrl("sizing-support")}
                      docLabel="Sizing and support"
                      label="About organization concurrency"
                    >
                      {"How many conversations your organization can run at " +
                        "once. It is a ceiling, not a guarantee — platform " +
                        "capacity can hold you below it, and a batch can ask " +
                        "for fewer when you launch it."}
                    </HelpTip>
                  </Inline>
                ),
                value:
                  limits?.organization_concurrency_max != null
                    ? String(limits.organization_concurrency_max)
                    : "No organization limit",
              },
            ]}
          />
        </Stack>
      )}
    </Section>
  );

  return (
    <Page variant="config">
      {isPending ? (
        <Skeleton
          label="Loading organization settings"
          rows={4}
          shape="detail-field"
        />
      ) : null}
      {errorMessage ? <InlineError>{errorMessage}</InlineError> : null}
      {!isPending && !errorMessage && organization && me ? (
        <Stack gap={6}>
          <Section heading="Profile">
            {isOwner ? (
              <form onSubmit={handleSubmit}>
                <Stack gap={3}>
                  <Inline align="end" className="marker-field-row" gap={3}>
                    <TextInput
                      label="Display name"
                      required
                      value={displayName}
                      onChange={(event) => {
                        onDisplayNameChange(event.currentTarget.value);
                      }}
                    />
                    <Button disabled={isSaveDisabled} type="submit">
                      Save
                    </Button>
                  </Inline>
                  {saveErrorMessage ? (
                    <InlineError>{saveErrorMessage}</InlineError>
                  ) : null}
                  {isSavePending ? (
                    <Inline gap={2}>
                      <Spinner label="Saving organization profile" />
                      <Text variant="muted">Saving…</Text>
                    </Inline>
                  ) : null}
                  {isSaved ? <Text role="status">Saved.</Text> : null}
                </Stack>
              </form>
            ) : (
              <Stack gap={2}>
                <KeyValue
                  rows={[
                    {
                      label: "Display name",
                      value: organization.display_name,
                    },
                  ]}
                />
                <Text variant="muted">Only the owner can edit this.</Text>
              </Stack>
            )}
          </Section>
          <Section heading="Details">
            <KeyValue
              rows={organizationDetailsRows(organization, me, ownerName)}
            />
          </Section>
          {limitsSection}
        </Stack>
      ) : null}
      {!isPending && !errorMessage && me && isOperator ? (
        <Stack gap={6}>
          <Section heading="Profile">
            <Stack gap={2}>
              <KeyValue
                rows={[
                  {
                    label: "Display name",
                    value: me.organization.name,
                  },
                  {
                    label: "Organization ID",
                    value: (
                      <Copyable
                        label="Organization ID"
                        value={me.organization.id}
                      />
                    ),
                  },
                ]}
              />
              <Text variant="muted">
                Deployment operator session. Customer organization settings are
                not available.
              </Text>
            </Stack>
          </Section>
          {limitsSection}
        </Stack>
      ) : null}
    </Page>
  );
}
